import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { SubmitHandler, useForm } from 'react-hook-form';

import type { IViewProps } from 'common/types/views';
import type { WithChildren } from 'common/types';

import { useGenericFieldsActionsAdopted, useGenericFieldsDataAdopted } from 'containers/FormDataAdapter';
import { useAssemblyGroup } from 'containers/AssemblyGroupStorage/context';
import {
  createGroup, addItem, completeGroup, removeItem as removeItemApi,
} from 'containers/AssemblyGroupStorage/api';
import { useAdditionalDataStorage } from 'containers/AdditionalDataStorage/context';
import { determineAndApplyErrorToForm } from 'utils/determineAndApplyErrorToForm';
import { showToast } from 'utils/toasts';
import { useCssVariables } from 'utils/css-variables';
import { scrollToEmptyInput } from 'utils/scrollToInput';
import { sendDataToParentIframe } from 'utils/send-data-to-parent-iframe';
import type { ICPQCart } from 'api/cart/types';

import FormWrapper from 'common/components/FormWrapper';
import ViewsButtonsBlock from 'common/components/ViewsButtonsBlock';
import BadgeButton from 'common/components/BadgeButton';
import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import { IFormValues } from 'common/components/FormHF/types';
import { prepareDefaultFormData } from 'common/components/FormHF/utils';
import { FormLoader } from 'common/components/Loader';
import { LengthType } from 'common/types/additional';
import { ToastTypes } from 'common/types/toasts';
import {
  STEP_ACCESSORIES,
  STEP_ANGLE,
  STEP_ASS_LENGTH,
  STEP_FITTING,
  STEP_HOSE,
} from '@greenline/containers/FormSwitcher/stepNames';
import { ARGProjectFields } from '@arg,@demo,@greenline/containers/project-fields';
import { getCommitAction } from 'common/constants';
import { CommitAction } from 'utils/commit-action';
import { extractSummaryData } from '@greenline/utils/extractSummaryData';
import { AssemblyGroupView } from '@greenline/views/AssemblyGroupView';
import { mapAssemblyGroupToEvent, mapSingleAssemblyToEvent } from '@greenline/api/cart/events-mappers';

import { readBool } from 'utils/readBool';
import { track } from 'utils/kitAnalytics';
import KitConfirmActions from 'common/components/kit-shared/KitConfirmActions';
import ConfirmDialog from 'common/components/kit-shared/ConfirmDialog';
import { buildPdfRenderPayload, isServerPdfEnabled } from 'common/components/kit-shared/pdfRenderPayload';
import { useKitLocked } from 'common/components/kit-shared/useKitLocked';
import type { ISaveAssemblyPayload } from 'containers/KitWorkspaceStorage/types';

import classNames from 'classnames';

import Rotation from '../../../common/components/Rotation';
import { useSessionId } from '../../../containers/GenericFieldsHandlingLayer/context';
import { getPDFLink } from '../../../api/requestFuncs';
import { useTranslateFn } from '../../../utils/useTranslateFn';

import TosCheckboxConnected from './components/TosCheckboxConnected';
import styles from './styles.module.scss';
import { PrintSection } from './PrintSection';
import { useSummary } from './useSummary';
import { Item } from './Item';
import GroupManagementBlock from './GroupManagementBlock';
import { DocumentTemplate } from './printer';

const SummaryView = ({
  onNextClickHandler,
  onBackClickHandler,
  onResetHandler,
  title,
  description,
  fieldsNames,
  onStepChange,
  children,
  isNextDisabled,
  isLoading: isCartLoading,
  lockNavigation,
}: WithChildren<IViewProps<ARGProjectFields>>) => {
  const idForm = 'form-summary';
  const t = useTranslateFn<string>();
  const { error, isLoading } = useGenericFieldsDataAdopted<IFormValues<ARGProjectFields>>();
  const { getFieldsByName } = useGenericFieldsActionsAdopted<IFormValues<ARGProjectFields>>();

  const initialFields = useMemo(() => getFieldsByName(fieldsNames), [getFieldsByName, fieldsNames]);

  const defaultValues = prepareDefaultFormData(initialFields);

  const defaultImageFitting = t('variables.fittingNoImage');
  const singleEventEnabled = readBool('variables.singleICPQCartEvent');
  const summaryEventSeparated = readBool('variables.summaryICPQCartEventSeparated');
  const singleMsgType = t('variables.singleICPQCartGroup') || '@cpq';
  const summaryMsgType = t('variables.summaryICPQCartGroup') || '@cpq';
  // Kit Builder toggle (per-collection, from rule_set.i18n.variables). Off keeps
  // the existing group flow unchanged.
  const kitsEnabled = readBool('variables.showManageKits');
  // Legacy assembly-group flow toggle (per-collection, from rule_set.i18n
  // .variables). Kit Builder wins when both are on; with both off the confirm
  // page falls back to the plain single-assembly flow (submit = Add to Cart).
  const groupFlowEnabled = readBool('variables.showAssemblyGroup');
  const plainFlow = !kitsEnabled && !groupFlowEnabled;
  const groupOnlyFlow = !kitsEnabled && groupFlowEnabled;
  // Once the kit-flow PDF is generated, lock the wizard form + nav (Back/Reset).
  const kitLocked = useKitLocked();
  const { data: additionalData } = useAdditionalDataStorage();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
    formState: { errors },
    setError,
  } = useForm<IFormValues<ARGProjectFields>>({
    defaultValues,
    mode: 'onChange',
  });

  const tosAgreed = watch('tosAgreed');

  useEffect(() => {
    if (tosAgreed) {
      scrollToEmptyInput();
    }
  }, [tosAgreed]);

  // eslint-disable-next-line no-unused-vars
  const onSubmitSuccess: SubmitHandler<IFormValues<ARGProjectFields>> = useCallback((data) => {
    if (tosAgreed) {
      onNextClickHandler();
    } else {
      setError('tosAgreed', { message: 'Please accept these terms' });
    }
  }, [onNextClickHandler, tosAgreed, setError]);

  useEffect(() => {
    if (error) {
      determineAndApplyErrorToForm(error, setError);
    }
  }, [error, setError]);

  const summary = useSummary();
  const {
    hose,
    fitting1,
    lengthInInches,
    lengthInFeet,
    lengthType,
    accessories,
    customerPartNumber,
    testing,
    fitting2,
    angle,
  } = summary;

  const sessionId = useSessionId();
  const cssRaw = useCssVariables();

  // Track confirm-page view once.
  useEffect(() => {
    if (kitsEnabled) track('assembly_confirm_viewed', { sessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitsEnabled]);

  // Build the standalone-assembly payload for the kit storage from the current
  // wizard state. Reuses the group flow's snapshot + HTML.
  const buildPayload = useCallback((): ISaveAssemblyPayload => ({
    cartData: { ...summary.cart, sessionId } as ISaveAssemblyPayload['cartData'],
    summaryData: extractSummaryData(summary),
    content: DocumentTemplate(summary, summary.quantity, t, cssRaw),
    name: summary.customerPartNumber || undefined,
    notes: summary.notes || undefined,
  }), [summary, sessionId, t, cssRaw]);

  // ============ ASSEMBLY GROUP STATE ============
  const {
    groupKey, group, setGroupKey, clearGroupKey, refetch,
  } = useAssemblyGroup();
  const [showAssemblyGroup, setShowAssemblyGroup] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  // Start over drops a finished order, so the group flow asks first. It is also
  // the only flow that hides the button until there is something to leave: the
  // kit flow has its own arrangement and the plain one keeps prod behaviour.
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Nothing to leave until the PDF exists, so the button stays out of the row.
  const hideStartOver = groupOnlyFlow && !pdfReady;
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isItemAdded, setIsItemAdded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingToCart, setIsSendingToCart] = useState(false);
  const itemsCount = group?.itemsCount ?? 0;

  // Clear stale completed group on SummaryView mount. Prevents showing old
  // assemblies in badge. Only clears if pdfReady=false (fresh visit), not
  // right after completing.
  useEffect(() => {
    if (group && group.status === 'completed' && !pdfReady) {
      clearGroupKey();
    }
  }, [group, pdfReady, clearGroupKey]);

  // Show toast deferred from "Add & Build Another" (with 60s staleness guard)
  useEffect(() => {
    const pending = localStorage.getItem('cpq_toast_after_reload');
    if (pending) {
      localStorage.removeItem('cpq_toast_after_reload');
      const ts = parseInt(pending, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < 60000) {
        showToast(ToastTypes.success, t('texts.assemblyGroup.toast.added'));
      }
    }
  }, [t]);

  // ============ ASSEMBLY GROUP HANDLERS ============
  const handleAddToGroup = useCallback(async () => {
    if (!sessionId) {
      showToast(ToastTypes.error, 'Configuration error: missing sessionId');
      return;
    }

    setIsAdding(true);
    const snap = extractSummaryData(summary);
    const singleHtml = DocumentTemplate(summary, summary.quantity, t, cssRaw);

    let key = groupKey;
    if (!key || (group && group.status !== 'draft')) {
      try {
        const { groupKey: newKey } = await createGroup(sessionId);
        setGroupKey(newKey);
        key = newKey;
        setPdfReady(false);
      } catch {
        showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
        setIsAdding(false);
        return;
      }
    }

    try {
      await addItem(key!, summary.cart, snap, singleHtml, sessionId);
      refetch();
      setIsItemAdded(true);
      if (lockNavigation) lockNavigation();
      showToast(ToastTypes.success, t('texts.assemblyGroup.toast.added'));
    } catch (e: any) {
      if (e?.status === 400) {
        showToast(ToastTypes.error, 'Maximum 50 assemblies allowed. Generate PDF to start a new group.');
      } else {
        showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
      }
    } finally {
      setIsAdding(false);
    }
  }, [sessionId, summary, groupKey, group, t, cssRaw, refetch, setGroupKey, lockNavigation]);

  const handleAddAndBuildAnother = useCallback(async () => {
    if (!sessionId) {
      showToast(ToastTypes.error, 'Configuration error: missing sessionId');
      return;
    }

    setIsAdding(true);
    const snap = extractSummaryData(summary);
    const singleHtml = DocumentTemplate(summary, summary.quantity, t, cssRaw);

    let key = groupKey;
    if (!key || (group && group.status !== 'draft')) {
      try {
        const { groupKey: newKey } = await createGroup(sessionId);
        setGroupKey(newKey);
        key = newKey;
      } catch {
        showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
        setIsAdding(false);
        return;
      }
    }

    try {
      await addItem(key!, summary.cart, snap, singleHtml, sessionId);

      if (singleEventEnabled && window.self !== window.parent) {
        try {
          const { message, redirectUrl, reset } = await sendDataToParentIframe<ICPQCart>(
            summary.cart,
            additionalData,
            mapSingleAssemblyToEvent,
            singleMsgType,
          );
          if (message) {
            // eslint-disable-next-line no-alert
            alert(message);
          }
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
          if (reset) {
            clearGroupKey();
          }
        } catch (err: any) {
          showToast(ToastTypes.error, String(err?.message || err));
        }
      }

      showToast(ToastTypes.success, t('texts.assemblyGroup.toast.added'));
      await new Promise((resolve) => { setTimeout(resolve, 1000); });
      onResetHandler();
    } catch (e: any) {
      if (e?.status === 400) {
        showToast(ToastTypes.error, 'Maximum 50 assemblies allowed. Generate PDF to start a new group.');
      } else {
        showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
      }
      setIsAdding(false);
    }
  }, [
    sessionId, summary, groupKey, group, t, cssRaw, onResetHandler, setGroupKey,
    singleEventEnabled, singleMsgType, additionalData, clearGroupKey,
  ]);

  const handleDeleteItem = useCallback(async (itemUuid: string) => {
    if (isDeleting || !groupKey) return;
    setIsDeleting(true);
    try {
      await removeItemApi(groupKey, itemUuid);
      showToast(ToastTypes.success, t('texts.assemblyGroup.toast.removed'));
      refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, groupKey, t, refetch]);

  const handleComplete = useCallback(async () => {
    if (!sessionId) return;
    setIsPdfGenerating(true);
    try {
      let url: string;

      if (groupKey && itemsCount > 0) {
        // Group flow: auto-add current assembly if not already added, then complete
        if (!isItemAdded) {
          const snap = extractSummaryData(summary);
          const singleHtml = DocumentTemplate(summary, summary.quantity, t, cssRaw);
          await addItem(groupKey, summary.cart, snap, singleHtml, sessionId, true);
        }
        const result = await completeGroup(
          groupKey,
          isServerPdfEnabled() ? buildPdfRenderPayload(extractSummaryData(summary), t) : undefined,
        );
        if (result.status !== 'completed') {
          showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
          return;
        }
        url = result.url;
        refetch();
      } else {
        // Single-item flow: get download URL (creates ClientSessionSnapshot + renders on open)
        url = await getPDFLink(
          sessionId,
          isServerPdfEnabled() ? buildPdfRenderPayload(extractSummaryData(summary), t) : undefined,
        );
      }

      setPdfReady(true);
      setDownloadUrl(url);
      if (lockNavigation) lockNavigation();
      if (groupKey && itemsCount > 0) {
        // For group flow, mark session as completed for analytics separately
        getPDFLink(sessionId).catch(() => {});
      }
      showToast(ToastTypes.success, t('texts.assemblyGroup.toast.pdfSuccess'));
    } catch {
      showToast(ToastTypes.error, t('texts.assemblyGroup.toast.pdfError'));
    } finally {
      setIsPdfGenerating(false);
    }
  }, [groupKey, itemsCount, isItemAdded, summary, t, cssRaw, refetch, sessionId, lockNavigation]);

  const handleEmailWithUrl = useCallback(() => {
    if (!downloadUrl) return;
    const subject = t('variables.contactEmailTitle');
    const email = t('variables.contactEmail');
    const a = document.createElement('a');
    a.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(downloadUrl)}`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  }, [downloadUrl, t]);

  const handleOpenPdf = useCallback(() => {
    if (!downloadUrl) return;
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }, [downloadUrl]);

  // Sends every assembly of the session to the parent cart: the completed
  // group's items, or just the current cart when no group was created
  // (single-assembly flow). handleComplete auto-adds the current assembly to
  // the group before completing, so group.items is the full session by now.
  const handleAddToCart = useCallback(async () => {
    if (window.self === window.parent) {
      showToast(ToastTypes.error, 'Cart is only available when embedded');
      return;
    }
    setIsSendingToCart(true);
    try {
      // Refetch so the dispatch sees the full group, including the assembly
      // auto-added by handleComplete (its refetch() is not awaited there).
      const freshItems = groupKey
        ? (await refetch()).data?.items ?? group?.items ?? []
        : [];
      const groupCarts = mapAssemblyGroupToEvent(freshItems);
      const carts: ICPQCart[] = groupCarts.length > 0 ? groupCarts : [summary.cart];
      if (summaryEventSeparated) {
        for (let i = 0; i < carts.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { message, redirectUrl, reset } = await sendDataToParentIframe<ICPQCart>(
            carts[i],
            additionalData,
            mapSingleAssemblyToEvent,
            summaryMsgType,
          );
          if (message) {
            // eslint-disable-next-line no-alert
            alert(message);
          }
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
          if (reset) {
            clearGroupKey();
            onResetHandler();
            return;
          }
        }
      } else {
        const { message, redirectUrl, reset } = await sendDataToParentIframe<ICPQCart[]>(
          carts,
          additionalData,
          (list) => list,
          summaryMsgType,
        );
        if (message) {
          // eslint-disable-next-line no-alert
          alert(message);
        }
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        if (reset) {
          clearGroupKey();
          onResetHandler();
          return;
        }
      }
    } catch (err: any) {
      showToast(ToastTypes.error, String(err?.message || err));
    } finally {
      setIsSendingToCart(false);
    }
  }, [
    groupKey, group, refetch, summary, summaryEventSeparated, summaryMsgType,
    additionalData, clearGroupKey, onResetHandler,
  ]);

  const nextCanBeUsed = getCommitAction() !== CommitAction.DISABLED;

  return (
    <FormWrapper
      header={title}
      description={description}
    >
      <form
        id={idForm}
        onSubmit={handleSubmit(onSubmitSuccess)}
        className="print"
        style={kitLocked ? { pointerEvents: 'none', opacity: 0.55 } : undefined}
      >
        {(isLoading || isCartLoading || isAdding) && <FormLoader />}
        <div className={styles.itemList}>
          <Item
            onClick={() => onStepChange(STEP_HOSE)}
            label="Hose Selection"
            title={hose?.title}
            image={hose?.attributes?.image || defaultImageFitting}
            description={hose?.attributes?.description}
          />
          <Item
            onClick={() => onStepChange(STEP_FITTING)}
            label="Fitting 1"
            title={fitting1?.title}
            image={fitting1?.attributes?.image || defaultImageFitting}
            description={fitting1?.attributes?.description}
          />
          <Item
            onClick={() => onStepChange(STEP_FITTING)}
            label="Fitting 2"
            title={fitting2?.title}
            image={fitting2?.attributes?.image || defaultImageFitting}
            description={fitting2?.attributes?.description}
          />
          <Item
            onClick={() => onStepChange(STEP_ASS_LENGTH)}
            label="Overall Assembly Length"
            title={lengthType === LengthType.feet ? (`${lengthInFeet} feet`) : (`${lengthInInches} inches`)}
          />
          {(typeof angle !== 'undefined') && (
            <>
              <br />
              <Rotation
                rotationSnap={360}
                rotationValue={angle}
                disabled
              />
              <Item
                onClick={() => onStepChange(STEP_ANGLE)}
                label="Angle of Rotation"
                title={`${angle}°`}
              />
            </>
          )}
          {accessories && (
            <Item
              onClick={() => onStepChange(STEP_ACCESSORIES)}
              label="Accessories"
              title={accessories.title}
              image={accessories.attributes?.image}
              description={accessories.attributes?.description}
            />
          )}
          {customerPartNumber && (
            <Item
              onClick={() => onStepChange(STEP_ACCESSORIES)}
              label="Customer Part Number"
              title={customerPartNumber}
            />
          )}
          {testing && (
            <Item
              onClick={() => onStepChange(STEP_ACCESSORIES)}
              label="Pressure Test and Certify Assembly"
              title={testing ? (
                <>
                  <svg
                    width="15"
                    height="12"
                    viewBox="0 0 15 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* eslint-disable-next-line */}
                  <path d="M12.6684 0.382523C12.9276 0.134418 13.2725 -0.00274376 13.6304 -2.12689e-05C13.9883 0.00270122 14.3311 0.145096 14.5865 0.397115C14.842 0.649135 14.9901 0.991068 14.9995 1.35076C15.009 1.71046 14.8791 2.05978 14.6372 2.32502L7.29535 11.5565C7.16911 11.6932 7.01674 11.8029 6.84735 11.8791C6.67797 11.9552 6.49505 11.9962 6.30954 11.9997C6.12402 12.0032 5.93972 11.969 5.76764 11.8992C5.59557 11.8294 5.43927 11.7254 5.30807 11.5935L0.439241 6.6984C0.303652 6.57138 0.194899 6.4182 0.119471 6.248C0.0440431 6.0778 0.00348419 5.89407 0.000214773 5.70777C-0.00305464 5.52147 0.0310325 5.33642 0.100442 5.16365C0.169851 4.99088 0.273161 4.83394 0.404209 4.70219C0.535257 4.57043 0.691357 4.46657 0.863198 4.39678C1.03504 4.327 1.2191 4.29273 1.4044 4.29602C1.5897 4.2993 1.77245 4.34008 1.94173 4.41591C2.11102 4.49175 2.26337 4.60109 2.38972 4.73741L6.24283 8.60944L12.6334 0.423223C12.6449 0.40898 12.6572 0.395392 12.6702 0.382523H12.6684Z" fill="#2BB483" />
                  </svg>
                  <div>Performed to industry standards</div>
                </>
              ) : <div>Not performed</div>}
            />
          )}
        </div>

        <GroupManagementBlock
          onAddToGroup={handleAddToGroup}
          onAddAndBuildAnother={handleAddAndBuildAnother}
          onStartOver={onResetHandler}
          isEditing={false}
          isAtLimit={itemsCount >= 50}
          isAdding={isAdding}
          isCompleted={pdfReady}
        />

        <div className={styles.tos}>
          <TosCheckboxConnected
            control={control}
            watch={watch}
            setValue={setValue}
            disabled={pdfReady}
          />
        </div>
        {children}
      </form>
      <div className={styles.desktop}>
        <ViewsButtonsBlock
          idForm={idForm}
          onResetClickHandler={groupOnlyFlow ? () => setShowResetConfirm(true) : onResetHandler}
          onBackClickHandler={onBackClickHandler}
          isBackDisabled={pdfReady || isItemAdded || kitLocked}
          isResetDisabled={kitLocked}
          className={hideStartOver ? styles.hideReset : undefined}
          isNextHidden={!plainFlow}
          nextButtonText="texts.btn.complete"
          isNextDisabled={isNextDisabled || !tosAgreed || !nextCanBeUsed}
        />
      </div>
      <PrintSection summary={summary} showLinks={plainFlow} />
      {kitsEnabled && (
        <KitConfirmActions
          sessionId={sessionId}
          buildPayload={buildPayload}
          onResetHandler={onResetHandler}
          lockNavigation={lockNavigation}
          idForm={idForm}
          className={styles.assemblyGroupButtons}
        />
      )}
      {!kitsEnabled && groupFlowEnabled && (
        <div className={styles.assemblyGroupButtons}>
          <BadgeButton
            label={t('texts.assemblyGroup.viewButton')}
            count={itemsCount}
            onClick={() => setShowAssemblyGroup(true)}
            disabled={itemsCount === 0}
            schema={ButtonSchema.secondary}
            className={classNames(styles.button, styles.viewAssembliesBtn)}
          />
          {!pdfReady ? (
            <>
              <Button
                schema={ButtonSchema.secondary}
                size={ButtonSize.standard}
                className={classNames(styles.button, styles.viewAssembliesBtn)}
                onClick={handleAddAndBuildAnother}
                disabled={itemsCount >= 50 || isAdding || isPdfGenerating}
                data-ignore-scroll-to={1}
              >
                {t('texts.assemblyGroup.addGroupBuildAnother')}
              </Button>
              <Button
                form={idForm}
                schema={ButtonSchema.primary}
                size={ButtonSize.standard}
                className={styles.button}
                onClick={handleComplete}
                disabled={isPdfGenerating || !tosAgreed}
                data-submit-button
                data-ignore-scroll-to={0}
              >
                {isPdfGenerating
                  ? t('texts.assemblyGroup.generating')
                  : t('texts.assemblyGroup.completeAssembly')}
              </Button>
            </>
          ) : (
            <>
              <Button
                form={idForm}
                schema={ButtonSchema.primary}
                size={ButtonSize.standard}
                className={styles.button}
                onClick={handleEmailWithUrl}
                data-ignore-scroll-to={1}
              >
                {t('texts.btn.emailBtn')}
              </Button>
              <Button
                form={idForm}
                schema={ButtonSchema.primary}
                size={ButtonSize.standard}
                className={styles.button}
                onClick={handleOpenPdf}
                data-ignore-scroll-to={1}
              >
                {t('texts.assemblyGroup.openPdf')}
              </Button>
              <Button
                schema={ButtonSchema.primary}
                size={ButtonSize.standard}
                className={styles.addToCartBtn}
                onClick={handleAddToCart}
                disabled={!nextCanBeUsed || isSendingToCart}
                data-ignore-scroll-to={1}
              >
                {t('texts.btn.complete')}
              </Button>
            </>
          )}
        </div>
      )}
      <div className={styles.mobile}>
        <ViewsButtonsBlock
          idForm={idForm}
          onResetClickHandler={groupOnlyFlow ? () => setShowResetConfirm(true) : onResetHandler}
          onBackClickHandler={onBackClickHandler}
          isBackDisabled={pdfReady || isItemAdded}
          className={hideStartOver ? styles.hideReset : undefined}
          isNextHidden={!plainFlow}
          nextButtonText="texts.btn.complete"
          isNextDisabled={isNextDisabled || !tosAgreed || !nextCanBeUsed}
        />
      </div>

      {showResetConfirm && (
        <ConfirmDialog
          title={t('texts.assemblyGroup.startOverConfirm.title')}
          message={t('texts.assemblyGroup.startOverConfirm.message')}
          note={t('texts.assemblyGroup.startOverConfirm.note')}
          confirmLabel={t('texts.assemblyGroup.startOverConfirm.confirmBtn')}
          danger
          warn
          onConfirm={() => { setShowResetConfirm(false); onResetHandler(); }}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}

      {/* Bottom sheet overlay (legacy group flow only) */}
      {!kitsEnabled && groupFlowEnabled && showAssemblyGroup && createPortal(
        <AssemblyGroupView
          group={group}
          groupKey={groupKey}
          onClose={() => setShowAssemblyGroup(false)}
          onDelete={handleDeleteItem}
          isDeleting={isDeleting}
        />,
        document.getElementById('modal-root')!,
      )}
    </FormWrapper>
  );
};

// @ts-ignore
export default SummaryView;
